import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { GraduationCap, Briefcase, Globe, BookOpen, Info, Camera } from "lucide-react";

export const ProfessionalProfile = () => {
  const [education, setEducation] = useState("");
  const [experience, setExperience] = useState("");
  const [languages, setLanguages] = useState("");
  const [certifications, setCertifications] = useState("");
  const [aboutMe, setAboutMe] = useState("");
  const [consultationType, setConsultationType] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["professional-profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;

      // Set initial values
      setEducation(data.education || "");
      setExperience(data.experience || "");
      setLanguages(data.languages?.join(", ") || "");
      setCertifications(data.certifications?.join(", ") || "");
      setAboutMe(data.about_me || "");
      setConsultationType(data.consultation_type?.join(", ") || "");

      return data;
    },
  });

  const updateProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("profiles")
        .update({
          education,
          experience,
          languages: languages.split(",").map(lang => lang.trim()),
          certifications: certifications.split(",").map(cert => cert.trim()),
          about_me: aboutMe,
          consultation_type: consultationType.split(",").map(type => type.trim()),
        })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: "Profil mis à jour",
        description: "Vos informations ont été mises à jour avec succès",
      });

      queryClient.invalidateQueries({ queryKey: ["professional-profile"] });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de mettre à jour le profil",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mon profil professionnel</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <GraduationCap className="h-4 w-4" />
            Formation
          </label>
          <Textarea
            value={education}
            onChange={(e) => setEducation(e.target.value)}
            placeholder="Décrivez votre formation académique"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Expérience
          </label>
          <Textarea
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
            placeholder="Décrivez votre expérience professionnelle"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Langues parlées
          </label>
          <Input
            value={languages}
            onChange={(e) => setLanguages(e.target.value)}
            placeholder="Ex: Français, Anglais, Créole (séparées par des virgules)"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Certifications
          </label>
          <Input
            value={certifications}
            onChange={(e) => setCertifications(e.target.value)}
            placeholder="Listez vos certifications (séparées par des virgules)"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Info className="h-4 w-4" />
            À propos de moi
          </label>
          <Textarea
            value={aboutMe}
            onChange={(e) => setAboutMe(e.target.value)}
            placeholder="Parlez de vous et de votre approche professionnelle"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Types de consultation</label>
          <Input
            value={consultationType}
            onChange={(e) => setConsultationType(e.target.value)}
            placeholder="Ex: En personne, Téléconsultation (séparés par des virgules)"
          />
        </div>

        <Button onClick={updateProfile} className="w-full">
          Mettre à jour mon profil
        </Button>
      </CardContent>
    </Card>
  );
};